import { ChevronDown } from 'lucide-react';
import { useState, useMemo } from 'react';
import PhoneInput from 'react-phone-input-2';
import { toast } from 'react-toastify';
import 'react-phone-input-2/lib/style.css';

import {
  useChechkEmailMutation,
  useUpdateTenantProfileMutation
} from '../../services/adminAuditsApi/adminAuditApi';
import { cn } from '../../utils/tailwindMerge';
import Button from '../Common/Button';

import { OnboardingFormType } from './types';

type EditTenantProps = {
  selectedTenant: OnboardingFormType;
  isEdit: boolean;
  setIsEdit: (val: boolean) => void;
};

const EditTenant = ({ selectedTenant, isEdit, setIsEdit }: EditTenantProps) => {
  const [editedData, setEditedData] =
    useState<OnboardingFormType>(selectedTenant);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [updateTenantProfile, { isLoading: isCreating }] =
    useUpdateTenantProfileMutation();
  const [checkEmail] = useChechkEmailMutation();
  const [planError, setPlanError] = useState('');

  const availableDashboards = [
    { id: 9001, name: 'Company Tests' },
    { id: 9002, name: 'Tech Interview' },
    { id: 9003, name: 'HR Interview' },
    { id: 9004, name: 'Code Sense AI' },
    { id: 9006, name: 'Schedule Test' },
    { id: 9007, name: 'Custom Written Test' },
    { id: 9008, name: 'Drive Test' },
    { id: 9009, name: 'Cloud Prep' }
  ];

  const isValidEmail = (email = '') =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const isValidIndianPhone = (phone = '') => {
    const normalized = phone.replace(/\s+/g, '');
    if (/^\+91[6-9]\d{9}$/.test(normalized)) return true;
    if (/^0[6-9]\d{9}$/.test(normalized)) return true;
    if (/^[6-9]\d{9}$/.test(normalized)) return true;
    return false;
  };

  const handleInputChange = (e: any) => {
    const { name, value, type } = e.target;

    const newValue =
      type === 'number' ? (value === '' ? '' : parseInt(value)) : value;

    setEditedData(prev => {
      const updated = { ...prev, [name]: newValue };

      if (name === 'planInDays') {
        if (newValue === '') {
          setPlanError('');
          updated.extendedPlanDays = null;
          return updated;
        }

        if (newValue < selectedTenant?.planInDays) {
          setPlanError(
            `New plan days cannot be less than current plan (${selectedTenant?.planInDays} days)`
          );
          updated.extendedPlanDays = 0;
        } else {
          setPlanError('');
          updated.extendedPlanDays = newValue - selectedTenant?.planInDays;
        }
      }

      return updated;
    });
  };

  const handlePhoneChange = (value: string, country: any) => {
    const formatted = value.startsWith('+') ? value : `+${value}`;
    setEditedData(prev => ({ ...prev, phoneNumber: formatted }));
  };

  const toggleDashboard = (dashboardId: number) => {
    setEditedData(prev => ({
      ...prev,
      dashboards: prev.dashboards.includes(dashboardId)
        ? prev.dashboards.filter(id => id !== dashboardId)
        : [...prev.dashboards, dashboardId]
    }));
  };

  const getSelectedDashboardNames = () => {
    return (
      availableDashboards
        .filter(d => editedData.dashboards.includes(d.id))
        .map(d => d.name)
        .join(', ') || 'Select dashboards'
    );
  };

  const hasFormChanged = useMemo(() => {
    const copyEditForm = structuredClone(editedData?.dashboards);
    const copySelectedForm = structuredClone(editedData?.dashboards);
    return (
      editedData.tenantName !== selectedTenant.tenantName ||
      editedData.dbName !== selectedTenant.dbName ||
      editedData.emailId !== selectedTenant.emailId ||
      editedData.phoneNumber !== selectedTenant.phoneNumber ||
      editedData.address !== selectedTenant.address ||
      editedData.institutionName !== selectedTenant.institutionName ||
      editedData.noOfSeats !== selectedTenant.noOfSeats ||
      editedData.planInDays !== selectedTenant.planInDays ||
      JSON.stringify(copyEditForm?.toSorted()) !==
        JSON.stringify(copySelectedForm?.toSorted())
    );
  }, [editedData, selectedTenant]);

  const isFormValid = useMemo(() => {
    return (
      editedData.tenantName.trim() !== '' &&
      editedData.dbName.trim() !== '' &&
      isValidEmail(editedData.emailId) &&
      isValidIndianPhone(editedData.phoneNumber) &&
      editedData.address.trim() !== '' &&
      editedData.institutionName.trim() !== '' &&
      Number(editedData.noOfSeats) > 0 &&
      Array.isArray(editedData.dashboards) &&
      editedData.dashboards.length > 0 &&
      Number(editedData.planInDays) > 0 &&
      hasFormChanged
    );
  }, [editedData]);

  const onBoardTenant = async () => {
    try {
      await updateTenantProfile({
        userId: selectedTenant?.id,
        body: editedData
      }).unwrap();
      setIsEdit(false);
      toast.success('Tenant updated successfully');
    } catch (error) {
      console.error('Error during updaing:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  return (
    <div className='h-full bg-gradient-to-br from-blue-50 to-indigo-100 p-8'>
      {/* <div className='mx-auto max-w-4xl rounded-2xl bg-white p-8 shadow-xl'> */}
      <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
        <div>
          <label className='mb-2 block text-sm font-semibold text-gray-700'>
            Tenant Name
          </label>
          <input
            type='text'
            name='tenantName'
            value={editedData.tenantName}
            onChange={handleInputChange}
            className='w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
          />
        </div>

        <div>
          <label className='mb-2 block text-sm font-semibold text-gray-700'>
            Database Name
          </label>
          <input
            disabled={isEdit}
            type='text'
            name='dbName'
            value={editedData.dbName}
            onChange={handleInputChange}
            className='w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
          />
        </div>

        <div>
          <label className='mb-2 block text-sm font-semibold text-gray-700'>
            Email ID
          </label>
          <input
            disabled={isEdit}
            type='email'
            name='emailId'
            value={editedData.emailId.toLowerCase()}
            onChange={handleInputChange}
            className='w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
          />
          {editedData.emailId !== '' && emailError !== '' && (
            <p className='mt-1 text-xs text-red-500'>{emailError}</p>
          )}
        </div>

        <div>
          <label className='mb-2 block text-sm font-semibold text-gray-700'>
            Phone Number
          </label>
          <PhoneInput
            disabled={isEdit}
            country={'in'}
            countryCodeEditable={false}
            value={editedData.phoneNumber}
            onChange={handlePhoneChange}
            inputClass={cn(
              isEdit ? '!bg-[#EBF0FA]' : '!bg-white',
              '!w-full !rounded-lg !border !border-gray-300 !px-4 !py-2  focus:!border-transparent focus:!ring-2 focus:!ring-blue-500'
            )}
            containerClass='w-full'
            specialLabel=''
          />
          {editedData.phoneNumber !== '91' &&
            !isValidIndianPhone(editedData.phoneNumber) && (
              <p className='mt-1 text-xs text-red-500'>
                Please enter a valid Indian mobile number.
              </p>
            )}
        </div>

        <div>
          <label className='mb-2 block text-sm font-semibold text-gray-700'>
            Address
          </label>
          <input
            type='text'
            name='address'
            value={editedData.address}
            onChange={handleInputChange}
            className='w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
          />
        </div>

        <div>
          <label className='mb-2 block text-sm font-semibold text-gray-700'>
            Institution Name
          </label>
          <input
            type='text'
            name='institutionName'
            value={editedData.institutionName}
            onChange={handleInputChange}
            className='w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
          />
        </div>

        <div>
          <label className='mb-2 block text-sm font-semibold text-gray-700'>
            Number of Seats
          </label>
          <input
            type='number'
            name='noOfSeats'
            value={editedData.noOfSeats}
            onChange={handleInputChange}
            className='w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
          />
        </div>

        <div>
          <label className='mb-2 block text-sm font-semibold text-gray-700'>
            Plan Duration (In Days)
          </label>

          <input
            type='number'
            name='planInDays'
            value={editedData.planInDays}
            onChange={handleInputChange}
            className='w-full rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none'
          />
        </div>
        {planError && (
          <div className='md:col-span-2'>
            <p className='-mt-4 w-full text-sm text-red-600'>{planError}</p>
          </div>
        )}
        <div className='md:col-span-2'>
          <label className='mb-2 block text-sm font-semibold text-gray-700'>
            Dashboards
          </label>
          <div className='relative'>
            <button
              type='button'
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className='flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500'
            >
              <span className='truncate'>{getSelectedDashboardNames()}</span>
              <ChevronDown
                className={`h-5 w-5 transition-transform ${
                  isDropdownOpen ? 'rotate-180' : ''
                }`}
              />
            </button>

            {isDropdownOpen && (
              <div className='absolute z-10 mt-2 max-h-60 w-full overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg'>
                {availableDashboards.map(dashboard => (
                  <label
                    key={dashboard.id}
                    className='flex cursor-pointer items-center px-4 py-3 hover:bg-gray-50'
                  >
                    <input
                      type='checkbox'
                      checked={editedData.dashboards.includes(dashboard.id)}
                      onChange={() => toggleDashboard(dashboard.id)}
                      className='h-4 w-4 text-blue-600 focus:ring-blue-500'
                    />
                    <span className='ml-3 text-gray-700'>{dashboard.name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className='flex justify-end md:col-span-2'>
          <Button
            label='Update'
            variant='contained'
            disabled={isCreating || planError !== ''}
            handleClickBtn={onBoardTenant}
          />
        </div>
      </div>
    </div>
  );
};

export default EditTenant;
